import { Slot } from '@radix-ui/react-slot'
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'

const DEFAULT_SELECTOR = 'h1,h2,h3,h4,h5,h6'

const FocusTransitionContext = React.createContext<{
  moveFocus: (to?: string | React.RefObject<HTMLElement>) => boolean
}>({
      moveFocus: () => false,
    })

function isRefWithHTMLElement (value: unknown): value is React.RefObject<HTMLElement> {
  return (
    value instanceof Object &&
    'current' in value &&
    value.current instanceof HTMLElement
  )
}

function ensureFocusable (element: HTMLElement) {
  if (element.tabIndex === -1 && element.getAttribute('tabIndex') === null) {
    element.setAttribute('tabIndex', '-1')
  }
}

function useDependencyRef<T = unknown> (dependency: T) {
  const ref = useRef<T>(dependency)
  ref.current = dependency

  return ref
}

interface UseFocusTransitionArgs {
  /**
   * Use the `skip` option to delay the focus move until the desired element
   * is available in the DOM. Useful, for example, if the component fetches
   * async data and renders a loading state before the desired element is
   * available in the DOM.
   */
  skip?: boolean;
  /**
   * Optionally identify the element to move focus to using a CSS selector or
   * a React `ref` object. By default, focus is moved to the first heading.
   */
  to?: string | React.RefObject<HTMLElement>;
}

/**
 * Use the `useFocusTransition` hook, in conjunction with the
 * `<FocusTransitionManager>` component, to move focus to the start of the new
 * content when the content changes without a page reload. For example, in a SPA
 * with client-side navigation, use this hook to move focus to the top of the
 * new page's main content (e.g. the first heading) after the new page's content
 * has been rendered.
 *
 * Use the `skip` option to delay moving focus if the desired element is not
 * rendered immediately, such as if the component renders a loading state while
 * fetching async data.
 *
 * The `<FocusTransitionManager>` component will ignore the first request to
 * move focus so that it does not interfere with normal focus handling of the
 * initially rendered content.
 */
export function useFocusTransition ({ skip = false, to }: UseFocusTransitionArgs = {}) {
  const { moveFocus } = useContext(FocusTransitionContext)

  const didMoveRef = useRef(false)

  // We're caching the `to` and `moveFocus` values to avoid stale values in the
  // `useEffect` callback without causing the effect to rerun if they change.
  const toRef = useDependencyRef(to)
  const moveFocusRef = useDependencyRef(moveFocus)

  useEffect(() => {
    if (!skip && !didMoveRef.current) {
      didMoveRef.current = moveFocusRef.current(toRef.current)
    }
  }, [skip])
}

interface FocusTransitionManagerProps {
  asChild?: boolean;
  children: React.ReactNode;
  moveFocusTo?: string | React.RefObject<HTMLElement>;
}

export function FocusTransitionManager ({ asChild = false, children, moveFocusTo }: FocusTransitionManagerProps) {
  const ref = useRef<HTMLDivElement>(null)
  const hasHandledInitialContent = useRef(false)

  /**
   * This callback is used by the `useFocusTransition` hook to move focus to the
   * desired element when the `<FocusTransitionManager>`'s content changes.
   * The callback returns a `boolean` to indicate whether or not the request to
   * move focus was handled, which the `useFocusTransition` hook uses to ensure
   * it does not move focus more than once during its component's lifecycle.
   *
   * We do not want to move focus for the initially rendered content, however,
   * so this callback will return `true` without moving focus the first time it
   * is called during the `<FocusTransitionManager>`'s lifecycle. For example,
   * in the case of a SPA, when the app first mounts we want the focus to start
   * on the body element so the user can explore the HTML document from the top.
   * It's only when the user navigates to a _new_ page that we want to move the
   * focus to the top of the new page's main content.
   */
  const moveFocus = useCallback(
    (to?: string | React.RefObject<HTMLElement>): boolean => {
      if (!hasHandledInitialContent.current) {
        hasHandledInitialContent.current = true

        // Even though the focus was not moved, return `true` so that the
        // `useFocusTransition` hook does not attempt to move focus again for
        // the initial content.
        return true
      }

      const selector = to || moveFocusTo || DEFAULT_SELECTOR

      let element: HTMLElement | null = null

      if (typeof selector === 'string') {
        element = ref.current?.querySelector(selector) as HTMLElement | null
      } else if (isRefWithHTMLElement(selector)) {
        element = selector.current
      }

      if (element) {
        ensureFocusable(element)
        element.focus()

        // Return `true` to indicate that focus was moved
        return true
      }

      return false
    },
    [moveFocusTo]
  )

  const value = useMemo(() => ({ moveFocus }), [moveFocus])

  const Component = asChild ? Slot : 'div'

  return (
    <FocusTransitionContext.Provider value={value}>
      <Component ref={ref}>{children}</Component>
    </FocusTransitionContext.Provider>
  )
}
